import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Avatar, Box, Button, Card, Checkbox, CircularProgress, FormControlLabel,
  IconButton, InputAdornment, Stack, TextField, Typography
} from '@mui/material';
import {
  BadgeOutlined, DarkMode, LightMode, LockOutlined, Person, Visibility, VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useThemeMode } from '../contexts/ThemeModeContext.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  remember: z.boolean()
});

function successTone() {
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.setValueAtTime(660, context.currentTime);
  oscillator.frequency.setValueAtTime(880, context.currentTime + 0.12);
  gain.gain.setValueAtTime(0.12, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.3);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.3);
}

export default function LoginPage() {
  const { login, rfidLogin } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const toast = useToast();
  const [rfidResult, setRfidResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const rfidRef = useRef();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '', remember: true }
  });

  useEffect(() => { rfidRef.current?.focus(); }, []);

  useEffect(() => {
    const firstError = Object.values(errors)[0]?.message;
    if (firstError) toast.warning(firstError);
  }, [errors, toast]);

  async function submit(credentials) {
    try {
      await login(credentials);
      toast.success('Signed in successfully');
    } catch (requestError) {
      toast.error(formatApiError(requestError, 'Unable to sign in'));
    }
  }

  async function scan(event) {
    if (event.key !== 'Enter' || !event.currentTarget.value.trim()) return;
    const rfid = event.currentTarget.value.trim();
    event.currentTarget.value = '';
    try {
      const result = await rfidLogin(rfid);
      setRfidResult(result);
      successTone();
      toast.success(result.token ? `Welcome ${result.employee.name}` : `Attendance recorded for ${result.employee.name}`);
    } catch (requestError) {
      setRfidResult(null);
      toast.error(formatApiError(requestError, 'RFID scan failed'));
    }
  }

  return (
    <Box sx={{
      height: '100dvh',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1.1fr .9fr' }
    }}>
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        p: 'clamp(24px, 6vh, 64px)',
        color: 'white',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: 'linear-gradient(145deg, #0d3b33 0%, #176b5b 60%, #2c8f7b 100%)'
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 54, height: 54, bgcolor: 'secondary.main', fontWeight: 800 }}>GE</Avatar>
          <Box><Typography variant="h5">GarmentERP</Typography><Typography sx={{ opacity: .75 }}>Manufacturing intelligence</Typography></Box>
        </Stack>
        <Box><Typography fontSize="clamp(2rem, 7vh, 3.75rem)" lineHeight={1.15} fontWeight={800} maxWidth={640}>From raw material to finished garment.</Typography>
          <Typography fontSize="clamp(.9rem, 2.3vh, 1.25rem)" mt="clamp(12px, 3vh, 24px)" sx={{ opacity: .75 }} maxWidth={560}>Production, workforce, inventory, orders, and true product cost in one secure workspace.</Typography>
        </Box>
        <Typography sx={{ opacity: .6 }}>GarmentERP · Operations platform</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 'clamp(12px, 4vh, 40px)', minHeight: 0 }}>
        <Card sx={{ width: '100%', maxWidth: 480, p: 'clamp(16px, 3.5vh, 40px)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Box>
              <Typography fontSize="clamp(1.5rem, 4vh, 2.125rem)" lineHeight={1.2} fontWeight={750}>Welcome back</Typography>
              <Typography color="text.secondary" fontSize="clamp(.8rem, 1.8vh, 1rem)" mt={0.25}>Sign in or scan your RFID card</Typography>
            </Box>
            <IconButton onClick={toggleMode} aria-label="Toggle dark mode" sx={{ mt: 0.5 }}>
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Stack>

          {rfidResult && <Alert severity="success" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={rfidResult.employee.photoPath}>{rfidResult.employee.name[0]}</Avatar>
              <Box><strong>{rfidResult.employee.name}</strong><br />
                {rfidResult.employee.department} · {rfidResult.employee.designation}<br />
                {new Date(rfidResult.scannedAt).toLocaleTimeString()}
              </Box>
            </Stack>
          </Alert>}

          <TextField inputRef={rfidRef} fullWidth size="small" label="RFID card scan" onKeyDown={scan}
            placeholder="Scan card and press Enter" sx={{ mt: 2, mb: 2 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><BadgeOutlined /></InputAdornment> } }} />

          <Stack direction="row" alignItems="center" spacing={2} mb={1.5}><Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }} />
            <Typography color="text.secondary" variant="body2">OR USE PASSWORD</Typography><Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }} /></Stack>

          <Box component="form" onSubmit={handleSubmit(submit)}>
            <TextField fullWidth size="small" label="Username" error={!!errors.username} helperText={errors.username?.message}
              {...register('username')} slotProps={{ input: { startAdornment: <InputAdornment position="start"><Person /></InputAdornment> } }} />
            <TextField fullWidth size="small" type={showPassword ? 'text' : 'password'} label="Password" error={!!errors.password} helperText={errors.password?.message}
              sx={{ mt: 1.5 }}
              {...register('password')} slotProps={{ input: {
                startAdornment: <InputAdornment position="start"><LockOutlined /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((visible) => !visible)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              } }} />
            <FormControlLabel sx={{ my: 0.25 }} control={<Checkbox size="small" {...register('remember')} defaultChecked />} label="Remember me" />
            <Button fullWidth type="submit" variant="contained" disabled={isSubmitting} sx={{ mt: 0.5, py: 1 }}>
              {isSubmitting ? <CircularProgress size={24} /> : 'Sign in'}
            </Button>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
